/**
 * AddEditQualificationRequisition Component
 * 
 * PURPOSE:
 * Manages welder qualification requisitions with multi-level approval workflow
 * 
 * FUNCTIONALITY:
 * 1. Create/Edit qualification requests for welders (New/Existing)
 * 2. Handle 3-tier approval workflow (Supervisor → Dept Head → QCE)
 * 3. Manage status transitions and email notifications
 * 4. Track qualification exams and position certifications
 * 
 * WORKFLOW STATES (FROM ROUTER):
 * 1. In Progress - Initial state, moving through approval hierarchy
 * 2. Approved - Final QCE approval, qualification valid
 * 3. Rejected - Rejected at any level, returns to initiator
 * 
 * STATUS FIELDS (ROUTER IMPLEMENTATION):
 * - wld_status: Overall workflow status ("In Progress", "Approved", "Rejected")
 * - wld_pendingStatus: Current approval stage ("Initiator", "Approver1", "Approver2", "Final", "Approved", "Rejected")
 * - wld_TrainingReqPendingWith: Email of current approver
 * 
 * APPROVAL WORKFLOW (3-TIER):
 * 1. Initiator creates request → Status: "In Progress", Pending: "Final" (Direct to QCE)
 * 2. QCE approves → Status: "Approved", Pending: "Approved"
 * 3. QCE rejects → Status: "Rejected", Pending: "Rejected", creates new revision
 * 4. Initiator can resubmit rejected requests (increments revision number)
 * 
 * API ENDPOINTS (FROM ROUTER):
 * 
 * 1. CREATE QUALIFICATION REQUEST:
 *    - Endpoint: POST /qualificationrequisition/
 *    - Router: QualificationRequisitionRouter.post("/")
 *    - Creates: Main request + Initial revision (Rev0)
 *    - Returns: Success/Error status
 * 
 * 2. GET QUALIFICATION DATA:
 *    - Endpoint: POST /qualificationrequisition/getTrainingReqData
 *    - Router: QualificationRequisitionRouter.post("/getTrainingReqData")
 *    - Filters: "initiated", "pending", "trainreq", "reqNumber"
 *    - Returns: Filtered qualification records
 * 
 * 3. WORKFLOW UPDATES:
 *    - Endpoint: POST /qualificationrequisition/workflow
 *    - Router: QualificationRequisitionRouter.post("/workflow")
 *    - Updates: Current revision, main request, creates new revision if rejected
 *    - Returns: Workflow update confirmation
 * 
 * 4. FINAL APPROVER MANAGEMENT:
 *    - Endpoint: POST /qualificationrequisition/FinalApprover
 *    - Router: QualificationRequisitionRouter.post("/FinalApprover")
 *    - Purpose: Add/update QCE email list for notifications
 *    - Returns: Update confirmation
 * 
 * 5. QCE DATA EXPORT:
 *    - Endpoint: GET /qualificationrequisition/qcegetalldataexport
 *    - Router: QualificationRequisitionRouter.get("/qcegetalldataexport")
 *    - Purpose: Export all qualification data for QCE reporting
 *    - Returns: Complete qualification dataset
 * 
 * INTEGRATION:
 * - Email notifications to QCE role users via WeldingReqEmail service
 * - User management for approver assignment
 * - Qualification requisition service for data operations
 * - Counter management for unique request numbering
 * 
 * USED BY:
 * - Qualification management workflows
 * - Welder certification processes  
 * - Quality control approval systems
 * - QCE reporting and export functions
 * - Multi-level approval workflow management
 * 
 * ==================== DYNAMODB STRUCTURE (FROM ROUTER IMPLEMENTATION) ====================
 * 
 * QUALIFICATION REQUISITION SYSTEM - 2 RECORD TYPES CREATED:
 * 
 * ==================== RECORD TYPE 1: MAIN QUALIFICATION REQUISITION ====================
 * 
 * CREATION LOCATION: QualificationRequisitionRouter.post("/") - Step 3
 * 
 * STRUCTURE:
 * - PK: "QualificationRequisition" (Fixed partition key)
 * - SK: "{userId}#{timestamp}" (Generated in frontend: `${userId}#${Math.floor(Date.now() / 1000)}#qual`)
 * 
 * ROUTER CODE REFERENCE:
 * ```javascript
 * // Step 3: Create main qualification request
 * const mainRequestResult = await admSvc.addQualificationRequest(requestData);
 * ```
 * 
 * COMPLETE DYNAMODB RECORD:
 * {
 *   "PK": "QualificationRequisition",
 *   "SK": "john.doe@company.com#1640995200#qual",
 *   "wld_TrainingReqEmployeeType": "Existing Welder",
 *   "wld_TrainingReqEmployeeName": "John Doe",
 *   "wld_TrainingReqSalaryEmployeeCode": "EMP001",
 *   "wld_TrainingReqEmployeeEmailID": "john.doe@company.com",
 *   "wld_TrainingReqCompanyName": "Godrej & Boyce Mfg. Ltd.",
 *   "wld_TrainingReqNumber": "Existing Welder/1025",
 *   "wld_WpqNo": "WPQ-2024-001",
 *   "wld_TrainingReqExperience": "5 years",
 *   "wld_ProcessQualified": "SMAW, GTAW",
 *   "wld_PositionQualified": [["F", "true", "Checked", "F"], ["H", "true", "Checked", "H"]],
 *   "wld_PositionQualified1": "F,H",
 *   "wld_BaseMaterial": "Carbon Steel",
 *   "wld_SupervisorName": "Jane Smith",
 *   "wld_SupervisorDept": "PED",
 *   "wld_WeldingDate": "15/01/2024",
 *   "wld_VisualExam": "Pass",
 *   "wld_NDEExam": "Pass",
 *   "wld_VisualExamDate": "16/01/2024",
 *   "wld_NDEExamDate": "17/01/2024",
 *   "wld_RTOfferWrench": "Pass",
 *   "wld_RTOfferWrenchDate": "18/01/2024",
 *   "wld_JointCategory": "Butt Joint",
 *   "wld_AssgnDept": "Production",
 *   "wld_Tpi": "Third Party Inspector",
 *   "wld_status": "In Progress",
 *   "wld_pendingStatus": "Final",
 *   "wld_TrainingReqPendingWith": "qce@company.com",
 *   "wld_TrainingReqApproverFinal": "",
 *   "wld_TrainingReqApproverFinalEmailId": "",
 *   "RevisionNo": 0,
 *   "wld_createdby": "john.doe",
 *   "wld_createdon": "15/01/2024",
 *   "wld_TrainingReqRequesterName": "John Doe",
 *   "wld_TrainingReqRequesterEmailId": "john.doe@company.com",
 *   "wld_TrainingReqRequesterDepartment": "PED",
 *   "wld_TrainingReqDate": "15/01/2024",
 *   "wld_ApprovedRejectedQualDate": ""
 * }
 * 
 * ==================== RECORD TYPE 2: CLOSURE REMARKS (APPROVAL WORKFLOW) ====================
 * 
 * CREATION LOCATION: QualificationRequisitionRouter.post("/") - Step 4
 * RESUBMISSION: QualificationRequisitionRouter.post("/workflow") - Step 3
 * 
 * STRUCTURE:
 * - PK: "{mainRequestSK}" (Same as main request SK for linking)
 * - SK: "Rev{N}" (Rev0, Rev1, Rev2... for revision tracking)
 * 
 * ROUTER CODE REFERENCE:
 * ```javascript
 * // Step 4: Create initial revision record
 * const initialRevisionRecord = {
 *   PK: requestData.SK,
 *   SK: "Rev0",
 *   wld_type: "Closure Remarks",
 *   RevisionNo: 0
 * };
 * await admSvc.addQualificationRequest(initialRevisionRecord);
 * ```
 * 
 * COMPLETE DYNAMODB RECORD:
 * {
 *   "PK": "john.doe@company.com#1640995200#qual",
 *   "SK": "Rev0",
 *   "wld_type": "Closure Remarks",
 *   "RevisionNo": 0,
 *   "wld_status": "Approved",
 *   "wld_pendingStatus": "Approved",
 *   "wld_TrainingReqApprover1": "Supervisor Name",
 *   "wld_TrainingReqApprover1EmailId": "supervisor@company.com",
 *   "wld_TrainingReqApprover1Comment": "Welder shows good skills",
 *   "wld_TrainingReqApprover1Status": "Approved",
 *   "wld_TrainingReqApprover1CreatedOn": "16/01/2024",
 *   "wld_TrainingReqApprover2": "Department Head",
 *   "wld_TrainingReqApprover2EmailId": "depthead@company.com",
 *   "wld_TrainingReqApprover2Comment": "Approved for qualification",
 *   "wld_TrainingReqApprover2Status": "Approved",
 *   "wld_TrainingReqApprover2CreatedOn": "17/01/2024",
 *   "wld_TrainingReqApproverFinal": "QCE Head",
 *   "wld_TrainingReqApproverFinalEmailId": "qce@company.com",
 *   "wld_TrainingReqApproverFinalComment": "Final approval granted",
 *   "wld_TrainingReqApproverFinalStatus": "Approved",
 *   "wld_TrainingReqApproverFinalCreatedOn": "18/01/2024",
 *   "wld_TrainingReqPendingWith": "qce@company.com"
 * }
 * 
 * ==================== ADDITIONAL SYSTEM RECORDS ====================
 * 
 * QUALIFICATION REQUEST COUNTER:
 * - PK: "QualificationRequestCount"
 * - SK: "1"
 * - QualCount: {number} (Auto-incremented)
 * 
 * FINAL APPROVER CONFIGURATION (QCE):
 * - PK: "QualificationRequisitionFinalApprover"
 * - SK: "Final"
 * - FinalEmailIds: "qce1@company.com,qce2@company.com,qce3@company.com"
 * 
 * ==================== ROUTER-BASED WORKFLOW OPERATIONS ====================
 * 
 * CREATION WORKFLOW (POST /):
 * 
 * STEP 1: Validate request data using Qualification_RequisitionSchema
 * STEP 2: Get qualification counter and increment
 * STEP 3: Create main qualification request (PK="QualificationRequisition")
 * STEP 4: Create initial revision record (PK=mainRequestSK, SK="Rev0")
 * STEP 5: Send email notification to QCE and stakeholders
 * 
 * APPROVAL WORKFLOW (POST /workflow):
 * 
 * STEP 1: Update current revision with approver decision and qualification details
 * STEP 2: Update main request with new status, revision number, and exam results
 * STEP 3: Create new revision if rejected (rejected → resubmission)
 * STEP 4: Send email notification with workflow status
 * 
 * QUERY PATTERNS (POST /getTrainingReqData):
 * 
 * FLAG="initiated": 
 * - Query: PK="QualificationRequisition" AND begins_with(SK, userId)
 * - Returns: User's initiated qualification requests
 * 
 * FLAG="pending":
 * - Query: PK="QualificationRequisition" AND wld_TrainingReqPendingWith=userId (GSI)
 * - Returns: Qualification requests pending user's approval
 * 
 * FLAG="trainreq":
 * - Query: PK="QualificationRequisition" AND SK=specificSK
 * - Returns: Specific qualification request
 * 
 * FLAG="reqNumber":
 * - Query: PK=requestNumber
 * - Returns: Closure remarks for specific qualification request
 * 
 * REVISION TRACKING IMPLEMENTATION:
 * 
 * INITIAL CREATION (Router POST /):
 * ```javascript
 * // Main record
 * { PK: "QualificationRequisition", SK: "user#timestamp#qual", RevisionNo: 0 }
 * 
 * // Initial revision
 * { PK: "user#timestamp#qual", SK: "Rev0", RevisionNo: 0 }
 * ```
 * 
 * RESUBMISSION AFTER REJECTION (Router POST /workflow):
 * ```javascript
 * // Update main record
 * { RevisionNo: newRevisionNumber, wld_ApprovedRejectedQualDate: currentDate }
 * 
 * // Create new revision
 * { PK: "user#timestamp#qual", SK: "Rev{N+1}", RevisionNo: newRevisionNumber }
 * ```
 * 
 * QUALIFICATION-SPECIFIC FEATURES:
 * 
 * POSITION QUALIFICATION TRACKING:
 * - Array format: [["position", "checked", "status", "position"]]
 * - Positions: F(Flat), H(Horizontal), V(Vertical), O(Overhead), SP(Special), 6G, 5G
 * - Flattened format: wld_PositionQualified1 = "F,H,V" (comma-separated)
 * 
 * EXAM RESULT TRACKING:
 * - Visual Examination: wld_VisualExam, wld_VisualExamDate
 * - NDE Examination: wld_NDEExam, wld_NDEExamDate
 * - RT Offer Wrench: wld_RTOfferWrench, wld_RTOfferWrenchDate
 * - Welding Test: wld_WeldingDate
 * 
 * WELDER TYPE HANDLING:
 * - New Welder: wld_TrainingReqSalaryEmployeeCode = "NA"
 * - Existing Welder: wld_TrainingReqSalaryEmployeeCode = actual employee code
 * 
 * APPROVAL HIERARCHY:
 * - Approver1: Direct supervisor (L+1)
 * - Approver2: Department head (WE role)
 * - ApproverFinal: QCE (Quality Control Engineer)
 * 
 * RECORD RELATIONSHIPS:
 * - Main request ↔ Closure remarks: Linked via PK=mainRequestSK
 * - All records share wld_TrainingReqNumber for reporting queries
 * - QCE email list maintained separately for bulk notifications
*/
import { useState, useEffect } from "react";
import { Button, Col, Form, Modal, Row, Alert } from "react-bootstrap";
import SuccessAlertModal from "../Modals/successAlertModal";
import ConfirmAlertModal from "../Modals/confirmAlertModal";
import {
  isAlphanumericWithSpace,
  isAlphanumericampspacedotdashbrackets,
  isAlphanumericWithDashDotSlashSpaceBrackets,
  isAlphanumericWithDotSpaceDashCommaBracketsAmpersand,
} from "../../functions/validations";
import { showSuccess, showWarning } from "../utility/ui/notifications/Toaster";
import { AddQualificationRequest, getQualReqByType, AddFinalApprover } from "../../Services/trainingRequisition-service";
import { getAllUsersByRole } from "../../Services/user-service";
import { RenderIf, formatDateDDMMYYYY, getDefaultDate, weldReqFormat } from "../../functions/welding-functions";
import { BiSelectMultiple } from "react-icons/bi";
import ClosureRemarksQual from "../utility/common/helpers/ClosureRemarksQual";
import SingleValuePopUp from "../utility/ui/modals/SingleValuePopUp";
import { DateInput } from "../utility/common/helpers/DateComponent";
import { maxDate } from "../utility/common/helpers/TodayDate";

/**
 * Mandatory field indicator component
 * Displays red text indicating all fields are required
 */
const MandatoryHeading = () => (
  <span style={{ color: "red", fontSize: "12px" }}>(All input fields are mandatory)</span>
);

/**
 * Main component for adding/editing qualification requisitions
 * 
 * PROPS:
 * - action: "Add" or "Edit" mode
 * - rowData: Existing data for edit mode
 * - onHide: Close modal callback
 * - setMainReload: Refresh parent component callback
 * 
 * FEATURES:
 * - Form validation with real-time error checking
 * - Dynamic field behavior based on welder type
 * - Position qualification checkboxes (F, H, V, O, SP, 6G, 5G)
 * - Date validation and formatting
 * - Modal-based success/confirmation dialogs
 */
const AddEditQualificationRequisition = (props) => {

  /**
   * Employee type constants for qualification requests
   * Determines form behavior and validation rules
   */
  const TrainingReqEmployeeType = {
    NewWelder: "New Welder",
    ExistingWelder: "Existing Welder",
  };
  // Date initialization for form defaults
  const todayDate = getDefaultDate();
  const todayDate1 = todayDate;
  
  // Component configuration
  let action = props.action; // "Add" or "Edit" mode
  let formName = "Welder Qualification Requisition";
  
  // Modal state management
  const [showSuccessModel, setShowSuccessModel] = useState(false); // Success confirmation modal
  const [showForm, setShowForm] = useState(false); // Form display state
  const [showConfirmModel, setshowConfirmModel] = useState(false); // Confirmation dialog
  const [alertMessage] = useState(""); // Alert message state
  const [setSubjectArr] = useState([]); // Subject array (unused)
  const [Flg, setFlg] = useState(false); // Edit mode flag
  
  /**
   * Popup state for single value input dialogs
   * Used for dynamic field editing
   */
  const [popUp, setPopUp] = useState({
    name: "",
    type: "",
    key: "",
    value: "",
  });

  // User session data
  let userId = sessionStorage.getItem("User"); // Current user email
  let username = sessionStorage.getItem("UserName"); // Current user name
  let department = "PED"; // Default department (Plant Engineering Department)

  /**
   * Fetch existing qualification request data for edit mode
   * 
   * @param {string} SK - Sort key identifier for the qualification request
   * @returns {Object} Qualification request data
   * 
   * FUNCTIONALITY:
   * - Retrieves qualification data by SK identifier
   * - Updates component state with fetched data
   * - Used when editing existing qualification requests
   */
  const getTraningReq = async (SK) => {
    let tmpObj = { fieldValue: SK, flag: "trainreq" };
    const Records = await getQualReqByType(tmpObj);
    if (Records != undefined) {
      setTrainReqObj(Records[0]);
      return Records[0];
    }
  };

  /**
   * Main state object for qualification request data
   * Contains all form fields and their values
   * Initialized differently for Add vs Edit modes
   */
  const [trainReqObj, setTrainReqObj] = useState([]);

  /**
   * Component initialization effect
   * 
   * FUNCTIONALITY:
   * - Edit Mode: Fetches existing qualification data
   * - Add Mode: Initializes form with default values
   * 
   * FORM INITIALIZATION:
   * - Sets default company name, dates, and user info
   * - Initializes position qualification array (F, H, V, O, SP, 6G, 5G)
   * - Pre-fills supervisor and requester information
   * - Sets up approval workflow fields
   */
  useEffect(() => {
    if (action == "Edit") {
      // Load existing qualification data for editing
      Promise.resolve(getTraningReq(props.rowData.SK));
    } else {
      // Initialize new qualification request with default values
      setTrainReqObj({
        // Basic qualification information
        wld_TrainingReqEmployeeType: "", // New/Existing welder selection
        wld_TrainingReqSalaryEmployeeCode: "", // Employee ID
        wld_WpqNo: "", // WPQ number
        wld_TrainingReqEmployeeName: "", // Welder name
        wld_TrainingReqCompanyName: "Godrej & Boyce Mfg. Ltd.", // Default company
        wld_TrainingReqExperience: "", // Welding experience
        wld_TrainingReqSubject: "", // Qualification subject
        wld_TrainingReqSubjectCode: "", // Subject code
        
        // Request metadata
        wld_TrainingReqDate: formatDateDDMMYYYY(todayDate), // Request date
        wld_TrainingReqRequesterName: username, // Current user as requester
        wld_TrainingReqRequesterEmailId: userId, // Requester email
        wld_TrainingReqRequesterDepartment: department, // Department
        wld_TrainingReqNumber: "", // Auto-generated request number
        wld_createdon: "", // Creation timestamp
        wld_createdby: username, // Creator
        
        // Approval workflow fields
        wld_TrainingReqApprover1: "", // First approver
        wld_TrainingReqApprover1EmailId: "", // First approver email
        wld_TrainingReqApprover2: "", // Second approver
        wld_TrainingReqApprover2EmailId: "", // Second approver email
        wld_TrainingReqApproverFinal: "", // Final approver (QCE)
        wld_TrainingReqApproverFinalEmailId: "", // Final approver email
        wld_TrainingReqPendingWith: "", // Current pending approver
        
        // Approval comments and status
        wld_TrainingReqApprover1Comment: "",
        wld_TrainingReqApprover1Status: "",
        wld_TrainingReqApprover2Comment: "",
        wld_TrainingReqApprover2Status: "",
        wld_TrainingReqApproverFinalComment: "",
        wld_TrainingReqApproverFinalStatus: "",
        
        // Qualification details
        wld_ProcessQualified: "", // Welding process
        
        /**
         * Position qualification array
         * Format: [value, checked, status, position]
         * Positions: F(Flat), H(Horizontal), V(Vertical), O(Overhead), SP(Special), 6G, 5G
         */
        wld_PositionQualified: [
          ["", "false", "Unchecked", "F"],
          ["", "false", "Unchecked", "H"],
          ["", "false", "Unchecked", "V"],
          ["", "false", "Unchecked", "O"],
          ["", "false", "Unchecked", "SP"],
          ["", "false", "Unchecked", "6G"],
          ["", "false", "Unchecked", "5G"],
        ],
        
        // Technical details
        wld_BaseMaterial: "", // Base material specification
        wld_SupervisorName: username, // Supervisor (default to current user)
        wld_SupervisorDept: department, // Supervisor department
        wld_WeldingDate: todayDate1, // Welding test date
        wld_VisualExam: "", // Visual examination results
        wld_NDEExam: "", // Non-destructive examination results
        wld_VisualExamDate: todayDate1, // Visual exam date
        wld_NDEExamDate: todayDate1, // NDE exam date
        
        // Additional fields
        wld_pendingSince: "", // Pending duration
        wld_CycleTime: "", // Process cycle time
        wld_NewWelder: "", // New welder flag
        wld_ExistingWelder: "", // Existing welder flag
        wld_JointCategory: "", // Joint category
        wld_AssgnDept: "", // Assigned department
        wld_Tpi: "", // Third party inspector
        wld_RTOfferWrench: "", // RT offer wrench
        wld_RTOfferWrenchDate: todayDate1, // RT offer date
        wld_PositionQualified1: "", // Additional position field
      });
    }
  }, []);

  /**
   * Edit mode flag setter
   * 
   * FUNCTIONALITY:
   * - Monitors when qualification data is loaded for edit mode
   * - Sets flag to enable form rendering with loaded data
   * - Prevents form display until data is fully loaded
   */
  useEffect(() => {
    if (action == "Edit" && trainReqObj.length != 0) {
      setFlg(true); // Enable form display in edit mode
    }
  }, [trainReqObj]);

  /**
   * Form validation error state
   * 
   * STRUCTURE:
   * Each field has: { status: boolean, message: string }
   * - status: true if error exists, false if valid
   * - message: Error message to display
   * 
   * VALIDATION COVERAGE:
   * - Required field validation
   * - Format validation (alphanumeric, dates, etc.)
   * - Business rule validation
   * - Real-time validation on field changes
   */
  const [error, setError] = useState({
    // Primary keys
    SK: {},
    PK: {},
    
    // Basic information fields
    wld_TrainingReqEmployeeType: {}, // New/Existing welder validation
    wld_TrainingReqSalaryEmployeeCode: {}, // Employee code validation
    wld_WpqNo: {}, // WPQ number validation
    wld_TrainingReqEmployeeName: {}, // Employee name validation
    wld_TrainingReqRequesterDepartment: {}, // Department validation
    wld_TrainingReqSubject: {}, // Subject validation
    wld_createdby: {}, // Creator validation
    wld_createdon: {}, // Creation date validation
    wld_TrainingReqCompanyName: {}, // Company name validation
    wld_TrainingReqExperience: {}, // Experience validation
    wld_TrainingReqDate: {}, // Request date validation
    wld_TrainingReqRequesterName: {}, // Requester name validation
    
    // Technical qualification fields
    wld_ProcessQualified: {}, // Process validation
    wld_PositionQualified: {}, // Position qualification validation
    wld_BaseMaterial: {}, // Base material validation
    wld_SupervisorName: {}, // Supervisor validation
    wld_SupervisorDept: {}, // Supervisor department validation
    wld_WeldingDate: {}, // Welding date validation
    wld_VisualExam: {}, // Visual exam validation
    wld_NDEExam: {}, // NDE exam validation
    wld_VisualExamDate: {}, // Visual exam date validation
    wld_NDEExamDate: {}, // NDE exam date validation
    
    // Additional fields
    wld_NewWelder: {}, // New welder flag validation
    wld_ExistingWelder: {}, // Existing welder flag validation
    wld_JointCategory: {}, // Joint category validation
    wld_AssgnDept: {}, // Assigned department validation
    wld_Tpi: {}, // TPI validation
    wld_RTOfferWrench: {}, // RT offer validation
    wld_RTOfferWrenchDate: {}, // RT offer date validation
  });

  /**
   * Callback to reload parent component after successful operation
   * 
   * FUNCTIONALITY:
   * - Closes current modal
   * - Triggers parent component refresh
   * - Used after successful add/edit operations
   */
  const callReload = () => {
    props.onHide();
    props.setMainReload(true);
  };

  /**
   * Success modal close handler
   * 
   * FUNCTIONALITY:
   * - Closes success confirmation modal
   * - Closes main qualification form modal
   * - Returns user to main qualification list
   */
  const handleCloseSuccess = () => {
    setShowSuccessModel(false);
    props.onHide();
  };
  
  /**
   * Confirmation modal close handler
   * Closes confirmation dialog without action
   */
  const handleCloseConf = () => {
    setshowConfirmModel(false);
  };

  /**
   * Employee type radio button change handler
   * 
   * FUNCTIONALITY:
   * - Handles New Welder vs Existing Welder selection
   * - Clears validation errors for employee type field
   * - Resets position qualification checkboxes
   * - Triggers form field updates based on selection
   * 
   * @param {Event} e - Radio button change event
   */
  const handleRadioChange = (e) => {
    if (e.target.value === TrainingReqEmployeeType.ExistingWelder || e.target.value === TrainingReqEmployeeType.NewWelder) {
      // Clear employee type validation error
      setError((prevState) => ({
        ...prevState,
        wld_TrainingReqEmployeeType: {
          status: false,
          message: "",
        }
      }));
      
      // Reset position qualification checkboxes to unchecked state
      setTrainReqObj(prevState => ({
        ...prevState,
        wld_PositionQualified: prevState.wld_PositionQualified.map(item => ["", "false", "Unchecked", item[3]])
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTrainReqObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "wld_TrainingReqEmployeeType") {
      let compName = "";
      if (value === TrainingReqEmployeeType.ExistingWelder) {
        compName = "Godrej & Boyce Mfg. Ltd.";



        setTrainReqObj((prevState) => ({
          ...prevState,
          ["wld_TrainingReqSalaryEmployeeCode"]: "",
          ["wld_TrainingReqEmployeeName"]: "",
          ["wld_TrainingReqApprover1"]: "",
          ["wld_TrainingReqApprover1EmailId"]: "",
          ["wld_TrainingReqPendingWith"]: "",
          ["wld_BaseMaterial"]: "",
          ["wld_TrainingReqExperience"]: "",
          ["wld_PositionQualified"[0][0]]: "",
          ["wld_PositionQualified"[1][0]]: "",

        }));
      } else {


        setTrainReqObj((prevState) => ({
          ...prevState,
          ["wld_TrainingReqSalaryEmployeeCode"]: "NA",
          ["wld_TrainingReqEmployeeName"]: "",
          ["wld_TrainingReqApprover1"]: "",
          ["wld_TrainingReqApprover1EmailId"]: "",
          ["wld_TrainingReqPendingWith"]: "",
          ["wld_BaseMaterial"]: "",
          ["wld_TrainingReqExperience"]: "",
          ["wld_PositionQualified"[0][0]]: "",

        }));
      }
      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqCompanyName: compName,
      }));
    }
  };


  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const setPopUpData = (data) => {


    setTrainReqObj((prevState) => ({
      ...prevState,
      ["wld_TrainingReqSalaryEmployeeCode"]: data.result.wld_code == undefined || data.result.wld_code == "" ? data.result.SK : data.result.wld_code,
      ["wld_TrainingReqEmployeeName"]: data.result.wld_name,
      ["wld_TrainingReqEmployeeEmailID"]: data.result.wld_email,
    }));
  };

  //Reset the data
  const resetForm = () => {

    if (action === "Add") {
      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqEmployeeType: "",
        wld_TrainingReqSalaryEmployeeCode: "",
        wld_TrainingReqEmployeeName: "",
        SK: "",
        PK: "",
        wld_TrainingReqCompanyName: "Godrej & Boyce Mfg. Ltd.",
        wld_TrainingReqExperience: "",
        wld_TrainingReqSubject: "",
        wld_TrainingReqSubjectCode: "",
        wld_TrainingReqDate: formatDateDDMMYYYY(todayDate),
        wld_TrainingReqRequesterName: username,
        wld_TrainingReqRequesterEmailId: userId,
        wld_TrainingReqRequesterDepartment: department,
        wld_TrainingReqNumber: "",
        wld_TrainingReqApprover1: "",
        wld_TrainingReqApprover1Status: "",
        wld_createdon: "",
        wld_createdby: "",
        wld_SupervisorName: "",
        wld_SupervisorDept: "",
        wld_ProcessQualified: "",
        wld_PositionQualified: [
          ["", "false", "Unchecked", "F"],
          ["", "false", "Unchecked", "H"],
          ["", "false", "Unchecked", "V"],
          ["", "false", "Unchecked", "O"],
          ["", "false", "Unchecked", "SP"],
          ["", "false", "Unchecked", "6G"],
          ["", "false", "Unchecked", "5G"],
        ],
        wld_BaseMaterial: "",
        wld_PositionQualified1: ""
      }));
      setSubjectArr([]);
    }

    setError((prevState) => ({
      ...prevState,
      SK: {},
      PK: {},
      wld_TrainingReqEmployeeType: {},
      wld_TrainingReqSalaryEmployeeCode: {},
      wld_TrainingReqEmployeeName: {},
      wld_TrainingReqRequesterDepartment: {},
      wld_TrainingReqSubject: {},
      wld_createdby: {},
      wld_createdon: {},
      wld_TrainingReqCompanyName: {},
      wld_TrainingReqExperience: {},
      wld_TrainingReqDate: {},
      wld_TrainingReqRequesterName: {},
    }));
  };

  const validateTrainingRequest = (action) => {
    let isError = false;
    let regExp = /^[-A-Za-z0-9\s.,&()]*$/;
    let regExpBM = /^[-A-Za-z0-9\s.,()&]*$/;
    let regExpEmpname = /^[A-Za-z0-9\s]*$/;
    let regVisual = /^[-A-Za-z0-9\s.()/]*$/;
    let quaPosi = trainReqObj.wld_PositionQualified;

    for (const position of quaPosi) {
      if (position[0] !== "") {
        isError = false;
        break;
      }
      else {
        isError = true;
      }
    }

    error.wld_PositionQualified = {
      status: true,
      message: true ? " Atleast one checkbox to be selected" : "",
    };

    if (trainReqObj.wld_TrainingReqEmployeeType === "") {
      isError = true;
      error.wld_TrainingReqEmployeeType = {
        status: true,
        message: "Employee Type is required.",
      };

    }
    if (!trainReqObj.wld_TrainingReqExperience.match(regExp)) {
      isError = true;
      error.wld_TrainingReqExperience = {
        status: true,
        message: "Special Characters not allowed",
      };
    }
    if (!trainReqObj.wld_TrainingReqCompanyName.match(regExp)) {
      isError = true;
      error.wld_TrainingReqCompanyName = {
        status: true,
        message: "Special Characters not allowed",
      };
    }
    if (!trainReqObj.wld_VisualExam.match(regVisual)) {
      isError = true;
      error.wld_VisualExam = {
        status: true,
        message: "Special Characters not allowed",
      };
    }
    if (!trainReqObj.wld_NDEExam.match(regVisual)) {
      isError = true;
      error.wld_NDEExam = {
        status: true,
        message: "Special Characters not allowed",
      };
    }
    if (!trainReqObj.wld_ProcessQualified.match(regExp)) {
      isError = true;
      error.wld_ProcessQualified = {
        status: true,
        message: "Special Characters not allowed",
      };
    }
    if (!trainReqObj.wld_TrainingReqEmployeeName.match(regExpEmpname)) {
      isError = true;
      error.wld_TrainingReqEmployeeName = {
        status: true,
        message: "Special Characters not allowed",
      };
    }

    if (!trainReqObj.wld_BaseMaterial.match(regExpBM)) {
      isError = true;
      error.wld_BaseMaterial = {
        status: true,
        message: "Special Characters not allowed",
      };
    }


    if (
      trainReqObj.wld_TrainingReqSalaryEmployeeCode.trim() === "" &&
      trainReqObj.wld_TrainingReqEmployeeType === "Company Employee"
    ) {
      trainReqObj.wld_TrainingReqSalaryEmployeeCode = "";
      isError = true;
      error.wld_TrainingReqSalaryEmployeeCode = {
        status: true,
        message: "Employee Code is required.",
      };
    }

    if (
      trainReqObj.wld_TrainingReqEmployeeName.trim() === "" ||
      trainReqObj.wld_TrainingReqEmployeeName === ""
    ) {
      trainReqObj.wld_TrainingReqEmployeeName = "";
      isError = true;
      error.wld_TrainingReqEmployeeName = {
        status: true,
        message: "Employee Name is required.",
      };
    }

    if (
      trainReqObj.wld_TrainingReqCompanyName.trim() === "" ||
      trainReqObj.wld_TrainingReqCompanyName === ""
    ) {
      trainReqObj.wld_TrainingReqCompanyName = "";
      isError = true;
      error.wld_TrainingReqCompanyName = {
        status: true,
        message: "Company Name is required.",
      };
    }
    if (trainReqObj.wld_TrainingReqExperience === "") {
      isError = true;
      error.wld_TrainingReqExperience = {
        status: true,
        message: "Experience is required.",
      };
    }

    if (trainReqObj.wld_ProcessQualified === "") {
      trainReqObj.wld_ProcessQualified = "";
      isError = true;
      error.wld_ProcessQualified = {
        status: true,
        message: "Process Qualified is required.",
      };
    }

    if (trainReqObj.wld_BaseMaterial === "") {
      trainReqObj.wld_BaseMaterial = "";
      isError = true;
      error.wld_BaseMaterial = {
        status: true,
        message: "Base Material is required.",
      };
    }

    if (trainReqObj.wld_SupervisorDept.trim() === "") {
      trainReqObj.wld_SupervisorDept = "";
      isError = true;
      error.wld_SupervisorDept = {
        status: true,
        message: "Godrej Supervisor Department is required.",
      };
    }

    if (trainReqObj.wld_SupervisorName.trim() === "") {
      trainReqObj.wld_SupervisorName = "";
      isError = true;
      error.wld_SupervisorName = {
        status: true,
        message: "Godrej Supervisor Name is required.",
      };
    }


    setError((prevState) => ({
      ...prevState,
      ...error,
    }));

    return isError;
  };

  const handleAdd = async (e) => {

    let result;
    let weldposi = trainReqObj?.wld_PositionQualified;
    if (action === "Add") {

      /*
      Add all the QCE email id from ROLE master to 
      Training table
      This is for sending mails to all QCE for approval
      */
      let QCEusers = await getAllUsersByRole([{ role: process.env.REACT_APP_ROLE_QCENGINEER }])
      let qceEmailid = ""
      QCEusers.map((i) => qceEmailid += i.SK + ",")
      let reqbody = { PK: "QualificationRequisitionFinalApprover", SK: "Final", FinalEmailIds: qceEmailid };
      await AddFinalApprover(reqbody)

      // Extract the first elements and filter out empty values
      const firstElements = weldposi && weldposi?.map(item => item[0]).filter(value => value !== "");

      // Join the elements with a comma
      let position = firstElements?.join(',');


      trainReqObj.wld_status = "In Progress";
      trainReqObj.wld_pendingStatus = "Final";
      trainReqObj.wld_TrainingReqNumber = weldReqFormat(todayDate);
      trainReqObj.PK = "QualificationRequisition";
      trainReqObj.SK = userId + "#" + Math.floor(Date.now() / 1000) + "#qual";
      trainReqObj.wld_createdby = username;
      trainReqObj.wld_createdon = new Date().toLocaleDateString("en-GB");
      trainReqObj.wld_ApprovedRejectedQualDate = ""
      trainReqObj.wld_TrainingReqPendingWith = process.env.REACT_APP_ROLE_QCENGINEER
      trainReqObj.wld_PositionQualified1 = position
      // trainReqObj.wld_CycleTime = String(wld_CycleTime)
      // trainReqObj.wld_pendingSince = String(wld_pendingSince)
      result = AddQualificationRequest(trainReqObj);

    }
    result
      .then((resp) => {
        if (resp.message === "Duplicate") {
          showWarning(resp.message);
        } else if (resp.message === "Success") {
          showSuccess(
            `Welder Qualification Request has been Initiated successfully`
          );
        }

        props.setMainReload(!props.mainReload);

        setShowForm(false);
        props.onHide();
      })
      .catch((error) => {
        console.log("Failed");
      });
  };

  function funcclose(closebool, param2) {
    if (closebool && param2 === "Success") {
      setShowSuccessModel(true);
      props.close(true);
    }

    if (closebool && param2 === "Cancel") {
      props.onHide();
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    let val = await validateTrainingRequest();
    if (!val) {
      setshowConfirmModel(true);
    }
  };




  const handleCheckboxChange = (position) => {
    setTrainReqObj(prevState => ({
      ...prevState,
      wld_PositionQualified: prevState.wld_PositionQualified.map(item => {
        if (item[3] === position) {
          const newValue = item[2] === "Unchecked" ? [position, "false", "Checked", position] : ["", "false", "Unchecked", position];
          return newValue;
        }
        return item;
      })
    }));
  };

  return (
    <>
      <SuccessAlertModal
        msg={`Closure Remarks Updated successfully.`}
        show={showSuccessModel}
        handleClose={handleCloseSuccess}
      />
      <ConfirmAlertModal
        action={action}
        handleYes={handleAdd}
        formName={formName}
        show={showConfirmModel}
        handleClose={handleCloseConf}
      />
      <Modal
        {...props}
        size="lg"
        centered
        className={
          showForm || showSuccessModel || showConfirmModel
            ? "opacity-0 userMasterModal"
            : "opacity-100 userMasterModal"
        }
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {action === "Add"
              ? (<>Add Qualification Request <MandatoryHeading /> </>)
              : "Welder Qualification Request Details"}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body>
            <div className="px-2 pb-2">
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <div className="m-input" style={{ width: 300 }}>
                      <span >Employee Type</span>
                      <div className="d-flex mb-2">
                        <div >
                          <input
                            type="radio"
                            name="wld_TrainingReqEmployeeType"
                            value={TrainingReqEmployeeType.ExistingWelder}
                            disabled={action !== "Add"}
                            checked={
                              trainReqObj.wld_TrainingReqEmployeeType ===
                              TrainingReqEmployeeType.ExistingWelder
                            }
                            onChange={(e) => {
                              handleRadioChange(e);
                              handleChange(e);
                            }}
                            style={{
                              fontSize: 10,
                              border: 20,
                              height: 10,
                            }}
                          />
                          <span
                            className={
                              trainReqObj.wld_TrainingReqEmployeeType ===
                                TrainingReqEmployeeType.ExistingWelder
                                ? "fontWeight "
                                : ""
                            }
                            style={{ fontSize: 12 }}
                          >
                            {TrainingReqEmployeeType.ExistingWelder}
                          </span>
                        </div>
                        <div className="ms-3">
                          <input
                            type="radio"
                            name="wld_TrainingReqEmployeeType"
                            value={TrainingReqEmployeeType.NewWelder}
                            disabled={action !== "Add"}
                            checked={
                              trainReqObj.wld_TrainingReqEmployeeType ===
                              TrainingReqEmployeeType.NewWelder
                            }
                            onChange={(e) => {
                              handleRadioChange(e);
                              handleChange(e);
                            }}
                            style={{
                              fontSize: 10,
                              border: 20,
                              height: 10,
                            }}
                          />
                          <span
                            className={
                              trainReqObj.wld_TrainingReqEmployeeType ===
                                TrainingReqEmployeeType.NewWelder
                                ? "fontWeight"
                                : ""
                            }
                            style={{ fontSize: 12 }}
                          >
                            {TrainingReqEmployeeType.NewWelder}
                          </span>

                        </div>
                      </div>
                      {error.wld_TrainingReqEmployeeType.status && (
                        <Form.Text >

                          <span
                            style={{
                              position: "relative",
                              bottom: "17px",
                              fontSize: "12px",
                              fontWeight: "normal",

                            }}
                            className="text-danger"
                          >
                            {error.wld_TrainingReqEmployeeType.message}
                          </span>

                        </Form.Text>)}
                    </div>
                  </Form.Group>
                </Col>
                {
                  trainReqObj.wld_TrainingReqEmployeeType ===
                  TrainingReqEmployeeType.ExistingWelder && (
                    <Col md={4}>
                      <Form.Group>
                        <div className="g-input" style={{ width: "100%" }}>
                          <input
                            id="wld_TrainingReqSalaryEmployeeCode"
                            name="wld_TrainingReqSalaryEmployeeCode"
                            type="text"
                            maxLength="100"
                            readOnly
                            className={
                              error.wld_TrainingReqSalaryEmployeeCode.status
                                ? "err-input"
                                : ""
                            }
                            placeholder=" "
                            color="var(--col-707070)"
                            style={{ width: "100%" }}
                            value={
                              trainReqObj.wld_TrainingReqSalaryEmployeeCode
                            }
                            onChange={handleChange}
                          />
                          <Form.Label
                            htmlFor="wld_TrainingReqSalaryEmployeeCode"
                          >
                            Salary / Employee Code
                          </Form.Label>

                          {action == "Add" && (<BiSelectMultiple
                            size={20}
                            onClick={() => {
                              showPopUp(
                                "Welder",
                                "Single",
                                "emailid",
                                trainReqObj.wld_TrainingReqSalaryEmployeeCode
                              );
                            }}
                            color={"gray"}
                            style={{
                              marginLeft: "15.5rem",
                              marginTop: "-4.2rem",
                            }}
                          />)}
                        </div>
                        {error.wld_TrainingReqSalaryEmployeeCode.status && (
                          <Form.Text className="text-danger">
                            <span
                              style={{
                                position: "relative",
                                bottom: "32px",
                                fontSize: "12px",
                              }}
                            >
                              {error.wld_TrainingReqSalaryEmployeeCode.message}
                            </span>
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                <Col md={4} style={{ height: "80px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        type="text"
                        id="wld_TrainingReqEmployeeName"
                        name="wld_TrainingReqEmployeeName"
                        placeholder=" "
                        readOnly={
                          action === "Edit" ||
                          trainReqObj.wld_TrainingReqEmployeeType ===
                          TrainingReqEmployeeType.Employee
                        }
                        maxLength="50"
                        className={
                          error.wld_TrainingReqEmployeeName.status
                            ? " g-input placeHolderInput inputText text-field-with-error"
                            : "g-input placeHolderInput inputText"
                        }
                        value={trainReqObj.wld_TrainingReqEmployeeName}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericWithSpace(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqEmployeeName: error,
                          }));
                        }}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqEmployeeName"
                      >
                        Employee Name
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqEmployeeName.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "20px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_TrainingReqEmployeeName.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                {(trainReqObj.wld_TrainingReqEmployeeType !==
                  TrainingReqEmployeeType.ExistingWelder) && (
                    <Col md={4}>
                    </Col>
                  )
                }
                <Col md={4} style={{ position: 'relative', bottom: "13px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        id="wld_TrainingReqCompanyName"
                        name="wld_TrainingReqCompanyName"
                        type="text"
                        maxLength="100"
                        className={
                          error.wld_TrainingReqCompanyName.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        style={{ width: "100%" }}
                        value={trainReqObj.wld_TrainingReqCompanyName}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericampspacedotdashbrackets(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqCompanyName: error,
                          }));
                        }}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqCompanyName"
                      >
                        Company Name
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqCompanyName.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "10px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_TrainingReqCompanyName.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>



                <Col md={4} style={{ position: 'relative', bottom: "15px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        id="wld_TrainingReqExperience"
                        name="wld_TrainingReqExperience"
                        type="text"
                        maxLength="50"
                        readOnly={
                          action === "Edit" &&
                          (props.calledFrom === "Initiated" ||
                            trainReqObj.wld_status !== "Rejected")
                        }
                        className={
                          error.wld_TrainingReqExperience.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        style={{ width: "100%" }}
                        value={trainReqObj.wld_TrainingReqExperience}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error =
                            isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqExperience: error,
                          }));
                        }}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqExperience"
                      >
                        Experience
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqExperience.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "10px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_TrainingReqExperience.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} style={{ position: 'relative', bottom: "15px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        id="wld_ProcessQualified"
                        name="wld_ProcessQualified"
                        type="text"
                        maxLength="50"
                        readOnly={
                          action === "Edit" &&
                          (props.calledFrom === "Initiated" ||
                            trainReqObj.wld_status !== "Rejected")
                        }
                        className={
                          error.wld_ProcessQualified.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        style={{ width: "100%" }}
                        value={trainReqObj.wld_ProcessQualified}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericampspacedotdashbrackets(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            wld_ProcessQualified: error,
                          }));
                        }}
                      />
                      <Form.Label
                        htmlFor="wld_ProcessQualified"
                      >
                        Welding Process to be Qualified
                      </Form.Label>
                    </div>
                    {error.wld_ProcessQualified.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "10px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_ProcessQualified.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} style={{ position: 'relative', bottom: "25px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      {trainReqObj.wld_PositionQualified?.map((position, index) => (
                        <div key={"div-" + position} style={{ display: 'inline-block', marginRight: '10px' }}>
                          <input
                            className="checkboxcolor"
                            style={{
                              position: "relative",
                              top: "30px",
                              marginRight: "7px",
                              marginLeft: "7px",
                              width: "15px",
                              height: "15px"
                            }}
                            type="checkbox"
                            id={`position_${position[3]}`}
                            name={`position_${position[3]}`}
                            value={position[3]}
                            checked={position[2] === "Checked"}
                            onChange={() => handleCheckboxChange(position[3])}

                          />
                          <Form.Label style={{ position: "relative", top: "18px", left: "22px" }}>
                            {position[3]} {/* Assuming the label is the fourth value in the array */}
                          </Form.Label>

                        </div>
                      ))}


                    </div>
                    <Form.Label style={{ position: "relative", bottom: "60px", fontSize: "14px", color: "var(--col-707070)", fontWeight: "500" }} >
                      Welding Position to be Qualified
                    </Form.Label>

                    {error.wld_PositionQualified.status && (
                      <Form.Text className="text-danger">
                        <p
                          style={{
                            position: "relative",
                            bottom: "33px",
                            left: "6px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_PositionQualified.message}
                        </p>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <>
                  <Col md={4} style={{ position: 'relative', bottom: "8px" }}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%" }}>
                        <input
                          id="wld_BaseMaterial"
                          name="wld_BaseMaterial"
                          type="text"
                          maxLength="50"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_BaseMaterial.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_BaseMaterial}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error =
                              isAlphanumericWithDotSpaceDashCommaBracketsAmpersand(
                                e.target.value
                              );
                            setError((prevState) => ({
                              ...prevState,
                              wld_BaseMaterial: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="wld_BaseMaterial">
                          Base Material
                        </Form.Label>
                      </div>
                      {error.wld_BaseMaterial.status && (
                        <Form.Text className="text-danger">
                          <span
                            style={{
                              position: "relative",
                              bottom: "10px",
                              fontSize: "12px",
                            }}
                          >
                            {error.wld_BaseMaterial.message}
                          </span>
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4} style={{ position: 'relative', bottom: "8px" }}>

                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        id="wld_TrainingReqDate"
                        readOnly
                        name="wld_TrainingReqDate"
                        type="text"
                        maxLength="20"
                        style={{ width: "100%" }}
                        className={
                          error.wld_TrainingReqDate.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        value={trainReqObj.wld_TrainingReqDate}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqDate"
                      >
                        Training Requisition Date
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqDate.status && (
                      <Form.Text className="text-danger">
                        {error.wld_TrainingReqDate.message}
                      </Form.Text>
                    )}

                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                        <input
                          id="wld_JointCategory"
                          name="wld_JointCategory"
                          type="text"
                          maxLength="35"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_JointCategory.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_JointCategory}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_JointCategory: error,
                            }));
                          }}
                        />
                        <Form.Label
                          htmlFor="wld_JointCategory"
                        >
                          Joint Category
                        </Form.Label>
                      </div>
                      {error.wld_JointCategory.status && (
                        <Form.Text className="text-danger">
                          {error.wld_JointCategory.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                        <input
                          id="wld_AssgnDept"
                          name="wld_AssgnDept"
                          type="text"
                          maxLength="35"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_AssgnDept.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_AssgnDept}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_AssgnDept: error,
                            }));
                          }}
                        />
                        <Form.Label
                          htmlFor="wld_AssgnDept"
                        >
                          Assigning Dept
                        </Form.Label>
                      </div>
                      {error.wld_AssgnDept.status && (
                        <Form.Text className="text-danger">
                          {error.wld_AssgnDept.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                        <input
                          id="wld_Tpi"
                          name="wld_Tpi"
                          type="text"
                          maxLength="35"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_Tpi.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_Tpi}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_Tpi: error,
                            }));
                          }}
                        />
                        <Form.Label
                          htmlFor="wld_Tpi"
                        >
                          TPI
                        </Form.Label>
                      </div>
                      {error.wld_Tpi.status && (
                        <Form.Text className="text-danger">
                          {error.wld_Tpi.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>




                  <Col md={4}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                        <input
                          id="wld_SupervisorName"
                          name="wld_SupervisorName"
                          type="text"
                          maxLength="50"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_SupervisorName.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_SupervisorName}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_SupervisorName: error,
                            }));
                          }}
                        />
                        <Form.Label
                          htmlFor="wld_SupervisorName"
                        >
                          Godrej Supervisor Name
                        </Form.Label>
                      </div>
                      {error.wld_SupervisorName.status && (
                        <Form.Text className="text-danger">
                          {error.wld_SupervisorName.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                        <input
                          id="wld_SupervisorDept"
                          name="wld_SupervisorDept"
                          type="text"
                          maxLength="50"
                          readOnly={
                            action === "Edit" &&
                            (props.calledFrom === "Initiated" ||
                              trainReqObj.wld_status !== "Rejected")
                          }
                          className={
                            error.wld_SupervisorDept.status
                              ? "placeHolderInput inputText text-field-with-error"
                              : "placeHolderInput inputText"
                          }
                          placeholder=" "
                          style={{ width: "100%" }}
                          value={trainReqObj.wld_SupervisorDept}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericampspacedotdashbrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_SupervisorDept: error,
                            }));
                          }}
                        />
                        <Form.Label
                          htmlFor="wld_SupervisorDept"
                        >
                          Godrej Supervisor Dept
                        </Form.Label>
                      </div>
                      {error.wld_SupervisorDept.status && (
                        <Form.Text className="text-danger">
                          {error.wld_SupervisorDept.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </>
              </Row>
            </div>
            {props.calledFrom === "Approval" && (
              <Row >
                <Form.Group>
                  <div className="g-input" style={{ width: "100%", bottom: "45px" }}>
                    <Col md={4} >
                      <DateInput
                        divClass="T-input w-100"
                        label="Welding Date"
                        className="groove_datecolor"
                        name="wld_WeldingDate"
                        todayDate={todayDate1}
                        obj={trainReqObj}
                        setobj={setTrainReqObj}
                        setError={setError}
                        status={error.wld_WeldingDate.status}
                        message={error.wld_WeldingDate.message}
                      />
                    </Col>
                  </div>
                </Form.Group>
              </Row>
            )}

            {props.calledFrom === "Approval" && action !== "Add" && (
              <Form.Group>
                <div className="g-input" style={{ width: "100%", bottom: "55px", height: "50px" }}>
                  <Row style={{ marginBottom: ".5rem" }}>

                    <Col md={8}>
                      <div className="g-input w-200">
                        <input
                          type="text"
                          id="wld_VisualExam"
                          name="wld_VisualExam"
                          placeholder=" "
                          style={{ width: "510px" }}
                          maxLength="100"
                          className={error.wld_VisualExam.status ? "err-input" : ""}
                          value={trainReqObj.wld_VisualExam}
                          onChange={handleChange}
                          required
                          onInput={(e) => {
                            let error = isAlphanumericWithDashDotSlashSpaceBrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_VisualExam: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="wld_VisualExam">Visual Examination</Form.Label>
                      </div>
                      {error.wld_VisualExam.status && (
                        <Form.Text className="text-danger">
                          <span style={{ position: "relative", bottom: "14px", fontSize: "12px" }}>
                            {error.wld_VisualExam.message}
                          </span>
                        </Form.Text>
                      )}
                    </Col>
                    <Col md={4}>
                      <DateInput
                        divClass="T-input w-100"
                        label=" Visual Examination Date"
                        className="groove_datecolor"
                        name="wld_VisualExamDate"
                        todayDate={todayDate1}
                        maxDate={maxDate}
                        obj={trainReqObj}
                        setobj={setTrainReqObj}
                        setError={setError}
                        status={error.wld_VisualExamDate.status}
                        message={error.wld_VisualExamDate.message}
                      />
                    </Col>
                  </Row>
                </div>
              </Form.Group>
            )}
            {props.calledFrom === "Approval" && action !== "Add" && (
              <Form.Group>
                <div className="g-input" style={{ width: "100%", bottom: "55px", height: "50px" }}>
                  <Row style={{ marginBottom: ".5rem" }}>

                    <Col md={8}>
                      <div className="g-input w-200">
                        <input
                          type="text"
                          id="wld_RTOfferWrench"
                          name="wld_RTOfferWrench"
                          placeholder=" "
                          style={{ width: "510px" }}
                          maxLength="100"
                          className={error.wld_RTOfferWrench.status ? "err-input" : ""}
                          value={trainReqObj.wld_RTOfferWrench}
                          onChange={handleChange}
                          required
                          onInput={(e) => {
                            let error = isAlphanumericWithDashDotSlashSpaceBrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_RTOfferWrench: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="wld_RTOfferWrench">RT Offer Wrench</Form.Label>
                      </div>
                      {error.wld_RTOfferWrench.status && (
                        <Form.Text className="text-danger">
                          <span style={{ position: "relative", bottom: "14px", fontSize: "12px" }}>
                            {error.wld_RTOfferWrench.message}
                          </span>
                        </Form.Text>
                      )}
                    </Col>

                    <Col md={4}>

                      <DateInput
                        divClass="T-input w-100"
                        label="RT Offer Wrench Date"
                        className="groove_datecolor"
                        name="wld_RTOfferWrenchDate"
                        todayDate={todayDate1}
                        maxDate={maxDate}
                        obj={trainReqObj}
                        setobj={setTrainReqObj}
                        setError={setError}
                        status={error.wld_RTOfferWrenchDate.status}
                        message={error.wld_RTOfferWrenchDate.message}
                      />
                    </Col>


                  </Row>
                </div>
              </Form.Group>
            )}



            {props.calledFrom === "Approval" && action != "Add" && (
              <Form.Group style={{ height: "0px" }}>
                <div className="g-input" style={{ width: "100%", bottom: "65px", height: "10px" }}>
                  <Row style={{ marginBottom: ".5rem" }}>
                    <Col md={8}>
                      <div className="g-input_Nde w-200" style={{ height: "30px", top: "8px" }}>
                        <input
                          type="text"
                          id="wld_NDEExam"
                          name="wld_NDEExam"
                          required
                          placeholder=" "
                          style={{ width: "510px" }}
                          maxLength="100"
                          className={error.wld_NDEExam.status ? "err-input" : ""}
                          value={trainReqObj.wld_NDEExam}
                          onChange={handleChange}
                          onInput={(e) => {
                            let error = isAlphanumericWithDashDotSlashSpaceBrackets(
                              e.target.value
                            );
                            setError((prevState) => ({
                              ...prevState,
                              wld_NDEExam: error,
                            }));
                          }}
                        />
                        <Form.Label htmlFor="wld_NDEExam">NDE Examination</Form.Label>
                      </div>
                      {error.wld_NDEExam.status && (
                        <Form.Text className="text-danger">
                          <span style={{ position: "relative", bottom: "8px", fontSize: "12px" }}>
                            {error.wld_NDEExam.message}
                          </span>
                        </Form.Text>
                      )}
                    </Col>
                    <Col md={4}>
                      <DateInput
                        divClass="T-input w-100"
                        label="NDE Examination Date"
                        className="groove_datecolor"
                        name="wld_NDEExamDate"
                        todayDate={todayDate1}
                        maxDate={maxDate}
                        obj={trainReqObj}
                        setobj={setTrainReqObj}
                        setError={setError}
                        status={error.wld_NDEExamDate.status}
                        message={error.wld_NDEExamDate.message}
                      />
                    </Col>
                  </Row>
                </div></Form.Group>
            )}
            {alertMessage && (
              <Alert className="alertBox" variant="warning">
                {alertMessage}
              </Alert>
            )}
            {action === "Add" && (
              <Row>
                <div>
                  <div
                    className={
                      action === "Edit"
                        ? "float-start m-1 invisible"
                        : "float-start m-1 visible"
                    }
                  >
                    <Button variant="outline-primary" onClick={resetForm} style={{ width: "80px", position: "relative", top: "8px" }}>
                      Reset
                    </Button>
                  </div>
                  <div className="float-end m-1">
                    <Button type="submit" className="btn-add-custom" style={{ width: "80px" }}>
                      {action === "Edit" ? "Update" : "Add"}
                    </Button>
                  </div>
                </div>
              </Row>
            )}
            {action === "Edit" && Flg && (
              <>

                <Row >
                  <Form.Group style={{ height: "10px" }}>
                    <div className="g-input" style={{ width: "100%", bottom: "5px" }}>
                      <u>Closure Remarks </u>
                    </div>
                  </Form.Group>
                  <ClosureRemarksQual
                    close={funcclose}
                    rowData={trainReqObj}
                    ExperienceNewRev={trainReqObj.wld_TrainingReqExperience}
                    callReload={callReload}
                    calledFrom={props.calledFrom}
                    setMainReload={props.setMainReload}
                    error={error}
                    setError={setError}
                  />
                </Row>

              </>
            )}
          </Modal.Body>
        </Form>
      </Modal>

      <RenderIf isTrue={popUp.type === "Single" && showForm}>
        <SingleValuePopUp
          show={showForm}
          onHide={() => setShowForm(false)}
          setSelected={setPopUpData}
          masterName={popUp.name}
          paramkey={popUp.key}
          paramvalue={popUp.value}
          backdrop="static"
        />
      </RenderIf>


    </>

  );
};

export default AddEditQualificationRequisition;
